/**
 * Case wallets — the case spine's wallets, listed on the Workbench canvas when
 * the Workbench was opened from a case (C-04 / NS-14).
 *
 * Reads GET /canvas/{id}/graph once per case (and again after a promotion
 * bumps the case's revision). A click hands the row to the Workbench, which
 * selects the canvas node or adds it from this payload — never a new request.
 */
import React, { useEffect, useMemo, useState } from 'react';
import { ChevronDown, ChevronUp, Loader2, RefreshCw, Wallet } from 'lucide-react';
import { fetchCaseGraph, type CaseGraph } from '../../services/caseCanvas';
import { useCaseSpineStore } from '../../stores/useCaseSpineStore';
import { caseWalletRows, findCanvasNodeId, shortAddress, type CaseWalletRow } from './caseWallets';

interface CanvasElementLike {
    data?: Record<string, any>;
}

export interface CaseWalletsPanelProps {
    caseId: string;
    /** Current canvas elements, for the "on canvas" marker. */
    canvasElements: readonly CanvasElementLike[];
    onSelectWallet: (row: CaseWalletRow) => void;
    defaultOpen?: boolean;
}

type LoadState =
    | { status: 'loading' }
    | { status: 'error'; message: string }
    | { status: 'loaded'; graph: CaseGraph };

const roleText = (row: CaseWalletRow): string | null =>
    row.placementRole || row.semanticRole || (row.type !== 'wallet' ? row.type : null);

export const CaseWalletsPanel: React.FC<CaseWalletsPanelProps> = ({ caseId, canvasElements, onSelectWallet, defaultOpen = false }) => {
    const [open, setOpen] = useState(defaultOpen);
    const [state, setState] = useState<LoadState>({ status: 'loading' });
    const [retry, setRetry] = useState(0);
    const revision = useCaseSpineStore((s) => s.revisions[caseId] || 0);

    // R8: a response for an earlier case never overwrites the current one.
    useEffect(() => {
        let cancelled = false;
        setState({ status: 'loading' });
        fetchCaseGraph(caseId, { level: 'low' })
            .then((graph) => { if (!cancelled) setState({ status: 'loaded', graph }); })
            .catch((e: unknown) => {
                if (!cancelled) setState({ status: 'error', message: e instanceof Error ? e.message : String(e) });
            });
        return () => { cancelled = true; };
    }, [caseId, revision, retry]);

    const rows = useMemo(() => (state.status === 'loaded' ? caseWalletRows(state.graph) : []), [state]);
    const onCanvasCount = useMemo(
        () => rows.filter((r) => findCanvasNodeId(canvasElements, r)).length,
        [rows, canvasElements],
    );

    const count = state.status === 'loaded' ? String(rows.length) : state.status === 'loading' ? '…' : '!';

    return (
        <div
            className="w-72 max-w-[80vw] rounded-lg border border-border bg-card/95 backdrop-blur-md shadow-overlay text-[11px]"
            data-tour="wb-case-wallets"
            data-testid="wb-case-wallets"
        >
            <button
                type="button"
                onClick={() => setOpen((v) => !v)}
                className="flex w-full items-center justify-between gap-2 px-2.5 py-1.5 font-semibold text-foreground hover:bg-muted/50 rounded-lg"
                aria-expanded={open}
                aria-controls="wb-case-wallets-list"
            >
                <span className="flex items-center gap-1.5">
                    <Wallet className="w-3.5 h-3.5 text-primary" />
                    Case wallets
                    <span className="rounded-full bg-primary/10 px-1.5 text-[10px] text-primary">{count}</span>
                </span>
                {open ? <ChevronUp className="w-3.5 h-3.5 text-muted-foreground" /> : <ChevronDown className="w-3.5 h-3.5 text-muted-foreground" />}
            </button>
            {open && (
                <div id="wb-case-wallets-list" className="border-t border-border/60 px-2 pb-2 pt-1.5">
                    {state.status === 'loading' && (
                        <div className="flex items-center gap-1.5 py-2 text-muted-foreground">
                            <Loader2 className="w-3.5 h-3.5 animate-spin" /> Loading the case graph…
                        </div>
                    )}
                    {state.status === 'error' && (
                        <div className="py-2 text-red-600 dark:text-red-400" role="alert">
                            Could not load the case graph: {state.message}
                            <button
                                type="button"
                                onClick={() => setRetry((n) => n + 1)}
                                className="ml-2 inline-flex items-center gap-1 underline text-foreground"
                            >
                                <RefreshCw className="w-3 h-3" /> Retry
                            </button>
                        </div>
                    )}
                    {state.status === 'loaded' && rows.length === 0 && (
                        <p className="py-2 text-muted-foreground">
                            No wallets on this case's graph yet. A Targeted Node Intelligence run in this case is
                            written to it when the run completes.
                        </p>
                    )}
                    {state.status === 'loaded' && rows.length > 0 && (
                        <>
                            <p className="pb-1 text-[10px] text-muted-foreground">
                                {onCanvasCount} of {rows.length} on the canvas. Click a wallet to select it; one that is not on
                                the canvas is added from the case graph.
                            </p>
                            <ul className="max-h-64 overflow-y-auto space-y-0.5" data-testid="wb-case-wallets-rows">
                                {rows.map((row) => {
                                    const onCanvas = !!findCanvasNodeId(canvasElements, row);
                                    const role = roleText(row);
                                    return (
                                        <li key={row.id}>
                                            <button
                                                type="button"
                                                onClick={() => onSelectWallet(row)}
                                                className="flex w-full items-center justify-between gap-2 rounded px-1.5 py-1 text-left hover:bg-muted"
                                                data-testid="wb-case-wallet-row"
                                                data-wallet-id={row.id}
                                                data-on-canvas={onCanvas ? 'true' : 'false'}
                                                title={row.address}
                                            >
                                                <span className="min-w-0">
                                                    <span className="block truncate font-medium text-foreground">
                                                        {row.label || row.entityName || shortAddress(row.address)}
                                                    </span>
                                                    <span className="block truncate font-mono text-[10px] text-muted-foreground">
                                                        {row.chain || 'chain unknown'} · {shortAddress(row.address)}
                                                        {role ? ` · ${role}` : ''}
                                                        {row.sourceLayer === 'graph_state' ? ' · from a saved snapshot' : ''}
                                                    </span>
                                                </span>
                                                <span
                                                    className={`shrink-0 rounded px-1 text-[10px] ${onCanvas
                                                        ? 'bg-emerald-500/10 text-emerald-700 dark:text-emerald-300'
                                                        : 'bg-muted text-muted-foreground'}`}
                                                >
                                                    {onCanvas ? 'on canvas' : 'add'}
                                                </span>
                                            </button>
                                        </li>
                                    );
                                })}
                            </ul>
                        </>
                    )}
                </div>
            )}
        </div>
    );
};
